import { useEffect, useState } from "react";
import { api, fmt, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LeadTimer } from "@/pages/Leads";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, Legend
} from "recharts";
import { Bus, Users, Route as RouteIcon, TrendingUp, AlertTriangle, Clock, Wallet, MessageCircle, ShieldAlert, Check, X as XIcon } from "lucide-react";

const COLORS = ["#F5A623", "#38BDF8", "#10B981", "#A78BFA", "#F472B6"];

function Kpi({ icon: Icon, label, value, sub, tone = "amber" }) {
  const toneMap = {
    amber: "text-amber-400",
    green: "text-emerald-400",
    red: "text-red-400",
    sky: "text-sky-400",
  };
  return (
    <Card className="p-5 bg-[hsl(224_45%_9%)] border-border card-hover">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg bg-white/5 grid place-items-center ${toneMap[tone]}`}><Icon className="w-5 h-5" /></div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      </div>
      <div className="mt-3 font-display text-3xl font-bold tracking-tight">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [paymentAlerts, setPaymentAlerts] = useState([]);
  const [deleteReqs, setDeleteReqs] = useState([]);
  const [reqTab, setReqTab] = useState("pending");
  const [reqCounts, setReqCounts] = useState({ pending: 0, approved: 0, rejected: 0 });

  async function loadDeleteReqs(status = reqTab) {
    try {
      const { data } = await api.get(`/delete-requests?status=${status}`);
      setDeleteReqs(data || []);
      const all = (await api.get("/delete-requests?status=all")).data || [];
      setReqCounts({
        pending: all.filter((r) => r.status === "pending").length,
        approved: all.filter((r) => r.status === "approved").length,
        rejected: all.filter((r) => r.status === "rejected").length,
      });
    } catch { setDeleteReqs([]); }
  }

  useEffect(() => {
    api.get("/stats/dashboard").then((r) => setStats(r.data));
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/notifications/final-payment-due").then((r) => setPaymentAlerts(r.data || [])).catch(() => {});
    if (user?.role === "admin") loadDeleteReqs("pending");
  }, [user?.role]);  // eslint-disable-line

  useEffect(() => { if (user?.role === "admin") loadDeleteReqs(reqTab); }, [reqTab]);  // eslint-disable-line

  async function approveDel(id, summary) {
    const ok = await confirm({
      title: "Approve delete?",
      description: `Are you sure you want to delete "${summary}"? It will move to Trash and can be restored within 30 days.`,
      confirmText: "Yes, delete", tone: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/delete-requests/${id}/approve`);
      toast.success("Deleted — moved to Trash");
      // Undo toast
      toast("Undo delete?", {
        duration: 6000,
        action: {
          label: "Restore",
          onClick: async () => {
            const trash = (await api.get("/trash")).data || [];
            const item = trash.find((t) => t.original_id === data.entity_id);
            if (item) { await api.post(`/trash/${item.id}/restore`); toast.success("Restored"); loadDeleteReqs(reqTab); }
          },
        },
      });
      loadDeleteReqs(reqTab);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }
  async function rejectDel(id, summary) {
    const ok = await confirm({
      title: "Reject delete request?",
      description: `The record "${summary}" will stay untouched.`,
      confirmText: "Reject", tone: "primary",
    });
    if (!ok) return;
    try { await api.post(`/delete-requests/${id}/reject`, { reason: "" }); toast.success("Request rejected"); loadDeleteReqs(reqTab); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  }

  if (!stats) return <div className="text-muted-foreground">Loading dashboard…</div>;

  const CLOSED = ["confirmed", "advance-received", "loss"];
  const activeLeads = leads.filter((l) => !CLOSED.includes(l.status)).sort((a, b) => {
    const at = new Date(a.entry_date || a.created_at).getTime();
    const bt = new Date(b.entry_date || b.created_at).getTime();
    return at - bt;
  });

  const revenue = stats.totals.revenue || 0;
  const expense = stats.totals.expense || 0;
  const margin = revenue ? Math.round((stats.totals.profit / revenue) * 100) : 0;

  const pieData = [
    { name: "Fuel + Maintenance", value: expense },
    { name: "Profit", value: Math.max(0, stats.totals.profit) },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-amber-300/80 mb-2">Command Center</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">Fleet Overview</h1>
          <p className="text-muted-foreground mt-2 max-w-lg text-sm">
            Real-time state of every vehicle, driver and rupee across your operation.
          </p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="border-amber-500/30 text-amber-300">Profit margin · {margin}%</Badge>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger">
        <Kpi icon={Bus} label="Vehicles" value={stats.counts.vehicles} sub={`${stats.expiring_vehicles.length} need attention`} />
        <Kpi icon={Users} label="Drivers" value={stats.counts.drivers} tone="sky" />
        <Kpi icon={RouteIcon} label="Trips logged" value={stats.counts.trips} sub={`${stats.counts.new_leads} new leads`} tone="green" />
        <Kpi icon={TrendingUp} label="Net profit" value={fmt(stats.totals.profit)} sub={`Rev ${fmt(revenue)}`} tone="amber" />
      </div>

      {/* Delete-approval queue (admin only) */}
      {user?.role === "admin" && (reqCounts.pending + reqCounts.approved + reqCounts.rejected) > 0 && (
        <Card className="p-5 bg-gradient-to-r from-rose-500/10 to-transparent border-rose-500/40" data-testid="delete-request-card">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <ShieldAlert className="w-4 h-4 text-rose-300"/>
            <div className="font-display text-lg font-bold">Delete requests</div>
            <div className="ml-auto inline-flex gap-1 flex-wrap">
              {[
                { k: "pending", label: "Pending", tone: "border-amber-500/40 text-amber-300 hover:border-amber-500" },
                { k: "approved", label: "Approved", tone: "border-emerald-500/40 text-emerald-300 hover:border-emerald-500" },
                { k: "rejected", label: "Rejected", tone: "border-slate-500/40 text-slate-300 hover:border-slate-400" },
              ].map((t) => (
                <button key={t.k} onClick={() => setReqTab(t.k)} data-testid={`del-req-tab-${t.k}`}
                  className={`px-3 py-1 rounded-md text-xs font-semibold border transition-colors ${reqTab === t.k ? "bg-white/10" : "bg-transparent"} ${t.tone}`}>
                  {t.label} <span className="opacity-70 ml-1">({reqCounts[t.k]})</span>
                </button>
              ))}
            </div>
          </div>
          {deleteReqs.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">No {reqTab} requests.</div>
          ) : (
            <div className="space-y-2">
              {deleteReqs.map((r) => (
                <div key={r.id} data-testid={`del-req-${r.id}`}
                  className="p-3 rounded-lg border border-rose-500/20 bg-[hsl(224_40%_11%)] flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm"><span className="font-mono text-amber-300">{r.entity_summary}</span></div>
                    <div className="text-[11px] text-muted-foreground">
                      Requested by <span className="text-slate-300">{r.requested_by_name || "—"}</span> ({r.requested_by_role}) · {new Date(r.requested_at).toLocaleString("en-GB")}
                    </div>
                    {r.status !== "pending" && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {r.status === "approved" ? "✔ Approved" : "✖ Rejected"} by <span className="text-slate-300">{r.decided_by_name || "—"}</span>
                        {r.decided_at ? ` · ${new Date(r.decided_at).toLocaleString("en-GB")}` : ""}
                        {r.reason ? ` · ${r.reason}` : ""}
                      </div>
                    )}
                  </div>
                  {r.status === "pending" && (
                    <div className="inline-flex gap-2">
                      <Button size="sm" onClick={() => approveDel(r.id, r.entity_summary)} data-testid={`del-req-approve-${r.id}`}
                        className="h-8 bg-emerald-500/90 hover:bg-emerald-500 text-[hsl(224_60%_6%)] font-semibold gap-1">
                        <Check className="w-3.5 h-3.5"/> Approve delete
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => rejectDel(r.id, r.entity_summary)} data-testid={`del-req-reject-${r.id}`}
                        className="h-8 border-border hover:border-red-500/40 hover:text-red-300 gap-1">
                        <XIcon className="w-3.5 h-3.5"/> Reject
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Final payment due — trips ending within 3 days with unpaid balance */}
      {paymentAlerts.length > 0 && (
        <Card className="p-5 bg-gradient-to-r from-red-500/10 to-transparent border-red-500/40" data-testid="final-payment-alerts">
          <div className="flex items-center gap-2 mb-3">
            <Wallet className="w-4 h-4 text-red-300"/>
            <div className="font-display text-lg font-bold">Final payment due — trips ending within 3 days</div>
            <Badge variant="outline" className="ml-auto border-red-500/40 text-red-300 text-xs">{paymentAlerts.length} pending</Badge>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {paymentAlerts.map((a) => {
              const wa = (a.client_whatsapp || a.client_mobile || "").replace(/[^0-9]/g, "");
              const msg = encodeURIComponent(
                `Hello ${a.client_name || "traveller"}, your trip ${a.trip_id} ends on ${a.end_date}. ` +
                `Please clear the remaining balance of ₹${(a.remaining_payable || 0).toLocaleString("en-IN")} before departure completes.`
              );
              return (
                <div key={a.id} data-testid={`final-pay-${a.id}`}
                  className="p-4 rounded-lg border border-red-500/30 bg-[hsl(224_40%_11%)] flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-amber-300 text-sm">{a.trip_id}</span>
                      <Badge className={`text-[10px] ${a.days_left <= 0 ? "bg-red-500/20 text-red-300 border-red-500/40" : "bg-amber-500/20 text-amber-300 border-amber-500/40"}`}>
                        {a.days_left === 0 ? "Ends today" : a.days_left === 1 ? "1 day left" : `${a.days_left} days left`}
                      </Badge>
                    </div>
                    <div className="text-sm mt-1 truncate">{a.client_name || "—"}</div>
                    <div className="text-[11px] text-muted-foreground">Ends {a.end_date}</div>
                    <div className="text-xs mt-1">Remaining <span className="font-mono text-red-300 font-semibold">₹{(a.remaining_payable || 0).toLocaleString("en-IN")}</span></div>
                  </div>
                  {wa && (
                    <a href={`https://wa.me/${wa}?text=${msg}`} target="_blank" rel="noreferrer"
                      data-testid={`final-pay-wa-${a.id}`}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs">
                      <MessageCircle className="w-3.5 h-3.5"/> Nudge
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 bg-[hsl(224_45%_9%)] border-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Monthly performance</div>
              <div className="font-display text-xl font-bold mt-1">Revenue vs Profit</div>
            </div>
          </div>
          <div className="h-64" data-testid="revenue-chart">
            <ResponsiveContainer>
              <BarChart data={stats.monthly_series} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0a1128", border: "1px solid #1f2937", borderRadius: 8 }} />
                <Bar dataKey="revenue" fill="#38BDF8" radius={[6,6,0,0]} />
                <Bar dataKey="profit" fill="#F5A623" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 bg-[hsl(224_45%_9%)] border-border">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Cost breakdown</div>
          <div className="font-display text-xl font-bold mt-1 mb-2">Where the money goes</div>
          <div className="h-48">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} innerRadius={45} outerRadius={72} paddingAngle={4} dataKey="value">
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0a1128", border: "1px solid #1f2937", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-2">
            {pieData.map((d, i) => (
              <div key={d.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{background:COLORS[i]}}/> {d.name}</span>
                <span className="font-mono text-amber-300">{fmt(d.value)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 bg-[hsl(224_45%_9%)] border-border">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <div className="font-display text-lg font-bold">Compliance alerts</div>
            <Badge variant="outline" className="ml-auto border-amber-500/30 text-amber-300 text-xs">{stats.expiring_vehicles.length} vehicles</Badge>
          </div>
          {stats.expiring_vehicles.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">All documents valid. Fleet is clear to go.</div>
          ) : (
            <div className="space-y-3">
              {stats.expiring_vehicles.map(({ vehicle, alerts }) => (
                <div key={vehicle.id} className="p-4 rounded-lg border border-border bg-[hsl(224_40%_11%)]">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-mono text-sm text-amber-300">{vehicle.registration_no}</div>
                      <div className="text-xs text-muted-foreground">{vehicle.vehicle_type} · {vehicle.vehicle_name}</div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 justify-end">
                      {alerts.map((a, i) => (
                        <Badge key={i} className={`text-[10px] font-normal ${a.status === "expired" ? "bg-red-500/20 text-red-300 border-red-500/40" : "bg-amber-500/20 text-amber-300 border-amber-500/40"}`}>
                          {a.type}: {a.status}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6 bg-[hsl(224_45%_9%)] border-border">
          <div className="font-display text-lg font-bold mb-4">Top cost vehicles</div>
          <div className="space-y-4">
            {stats.top_vehicle_costs.length === 0 ? (
              <div className="text-sm text-muted-foreground">No trip data yet.</div>
            ) : stats.top_vehicle_costs.map((v, i) => {
              const max = stats.top_vehicle_costs[0].cost || 1;
              return (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-mono text-amber-300">{v.vehicle}</span>
                    <span className="font-mono">{fmt(v.cost)}</span>
                  </div>
                  <Progress value={(v.cost / max) * 100} className="h-1.5" />
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <Card className="p-6 bg-[hsl(224_45%_9%)] border-border">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-4 h-4 text-amber-400"/>
          <div className="font-display text-lg font-bold">Live lead timers</div>
          <Badge variant="outline" className="ml-auto border-amber-500/30 text-amber-300 text-xs">{activeLeads.length} active</Badge>
        </div>
        {activeLeads.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">No open leads. Great job closing everything!</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {activeLeads.slice(0, 8).map((l) => (
              <div key={l.id} data-testid={`dash-lead-${l.id}`} className="p-4 rounded-lg border border-border bg-[hsl(224_40%_11%)] flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium truncate">{l.client_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{l.destination || "—"} · {l.lead_type?.toUpperCase() || "B2C"}</div>
                </div>
                <div className="text-right shrink-0">
                  <LeadTimer startIso={l.entry_date || l.created_at} status={l.status}/>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{l.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
